import { supabaseConfig } from '@/shared/config/supabase'
import { AppError } from '@/shared/lib/appError'

export {
  AI_EDGE_JSON_TIMEOUT_MS,
  AI_EDGE_STREAM_TIMEOUT_MS,
} from '@/shared/api/aiEdgeTimeoutContract'

export const AI_TIMEOUT_USER_MESSAGE =
  'A IA demorou mais que o esperado. Verifique sua conexão e tente novamente em instantes.'

export const AI_EDGE_HTML_RESPONSE_MESSAGE =
  'Não foi possível alcançar o serviço de IA. Verifique sua conexão ou tente novamente em instantes.'

export const AI_EDGE_INCOMPLETE_STREAM_MESSAGE =
  'A IA parou de responder antes de concluir. Tente novamente em instantes.'

export const AI_EDGE_WORKER_LIMIT_MESSAGE =
  'O serviço de IA atingiu o limite de processamento. Tente uma pergunta mais curta ou aguarde alguns instantes.'

/** Erros esperados de capacidade/cota — UI trata; não devem ir ao Sentry. */
export function isExpectedAiCapacityError(err: unknown): boolean {
  const status =
    err instanceof AppError
      ? err.status
      : typeof (err as { status?: number })?.status === 'number'
        ? (err as { status: number }).status
        : undefined
  if (status === 546 || status === 429) return true

  const msg = err instanceof Error ? err.message : String(err ?? '')
  if (/WORKER_RESOURCE_LIMIT/i.test(msg)) return true
  if (msg.includes(AI_EDGE_WORKER_LIMIT_MESSAGE)) return true
  if (/limite de processamento/i.test(msg)) return true
  if (/cota (do dia|de IA|diária)|quota exceeded|RESOURCE_EXHAUSTED|rate limit|too many requests/i.test(msg)) {
    return true
  }
  return false
}

export const mapAiEdgeErrorMessage = (raw: string, status?: number): string => {
  const msg = raw.trim()
  if (
    status === 546 ||
    /WORKER_RESOURCE_LIMIT/i.test(msg)
  ) {
    return AI_EDGE_WORKER_LIMIT_MESSAGE
  }
  return msg
}

export const isHtmlLikeContentType = (contentType: string): boolean => {
  const ct = contentType.toLowerCase()
  return ct.includes('text/html') || ct.includes('application/xhtml')
}

export const buildSupabaseEdgeFunctionUrl = (functionName: string): string => {
  const base = supabaseConfig.url.replace(/\/$/, '')
  const slug = functionName.replace(/^\/+|\/+$/g, '')
  if (!slug) {
    throw new AppError('Nome da Edge Function inválido.', { code: 'VALIDATION', status: 400 })
  }
  if (!supabaseConfig.isConfigured) {
    throw new AppError(
      'Supabase não configurado. Defina VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY.',
      { code: 'NOT_CONFIGURED', status: 503 },
    )
  }
  return `${base}/functions/v1/${slug}`
}

export const assertAiEdgeResponseContentType = (
  contentType: string,
  options?: { expectEventStream?: boolean },
): void => {
  if (isHtmlLikeContentType(contentType)) {
    throw new AppError(AI_EDGE_HTML_RESPONSE_MESSAGE, { code: 'HTML_RESPONSE', status: 502 })
  }
  if (options?.expectEventStream && contentType && !contentType.includes('text/event-stream')) {
    throw new AppError(AI_EDGE_INCOMPLETE_STREAM_MESSAGE, { code: 'INCOMPLETE', status: 502 })
  }
}

export const isAiFetchTimeout = (err: unknown, signal?: AbortSignal): boolean => {
  if (signal?.aborted && signal.reason === 'timeout') return true
  const name = (err as { name?: string })?.name
  if (name === 'TimeoutError') return true
  const msg = err instanceof Error ? err.message : String(err ?? '')
  return /timeout|timed out/i.test(msg)
}

export const toAiTimeoutAppError = (cause: unknown): AppError =>
  new AppError(AI_TIMEOUT_USER_MESSAGE, {
    code: 'TIMEOUT',
    status: 408,
    cause,
  })

export const toAiAbortAppError = (cause: unknown): AppError =>
  new AppError('Requisição de IA cancelada.', {
    code: 'ABORTED',
    status: 499,
    cause,
  })

export const mapAiFetchFailure = (
  err: unknown,
  signal?: AbortSignal,
): AppError => {
  if (isAiFetchTimeout(err, signal)) return toAiTimeoutAppError(err)
  if (
    (err as { name?: string })?.name === 'AbortError' ||
    signal?.aborted
  ) {
    return toAiAbortAppError(err)
  }
  if (err instanceof AppError) return err
  const msg = err instanceof Error ? err.message : String(err)
  return new AppError(msg || 'Falha na requisição de IA.', { cause: err })
}

export const createAiEdgeAbortSignal = (
  timeoutMs: number,
  externalSignal?: AbortSignal,
): { signal: AbortSignal; cleanup: () => void } => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort('timeout'), timeoutMs)
  const onExternalAbort = () => controller.abort(externalSignal?.reason ?? 'external-abort')

  if (externalSignal?.aborted) {
    onExternalAbort()
  } else {
    externalSignal?.addEventListener('abort', onExternalAbort, { once: true })
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId)
      externalSignal?.removeEventListener('abort', onExternalAbort)
    },
  }
}
